import React, { useState, useEffect } from "react";
import { ArrowRight, ShieldCheck, FileCheck2, Mail, Sparkles, Zap, Users2, Scale, UserRound } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { AvailabilityBar } from "@/components/site/AvailabilityBar";
import { TEAM_STAT_LABELS } from "@/i18n";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { ReportForm } from "@/components/report/ReportForm";
import { SuccessPanel } from "@/components/report/SuccessPanel";
import { TrackReport } from "@/components/report/TrackReport";
import { Button } from "@/components/ui/button";
import { EventsSection } from "@/components/common/EventsSection";

export default function Landing() {
  const { tr, lang } = useLang();
  const [result, setResult] = useState(null);
  const [team, setTeam] = useState({ stats: [], members: [] });

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const steps = [
    { icon: ShieldCheck, t: tr.how1t, d: tr.how1d },
    { icon: FileCheck2, t: tr.how2t, d: tr.how2d },
    { icon: Mail, t: tr.how3t, d: tr.how3d },
  ];

  const strategies = [
    { icon: Zap, t: tr.team1t, d: tr.team1d },
    { icon: Users2, t: tr.team2t, d: tr.team2d },
    { icon: Scale, t: tr.team3t, d: tr.team3d },
  ];

  return (
    <div id="top" className="min-h-screen bg-zinc-950 text-zinc-50">
      <SiteHeader />

      {/* comeback announcement */}
      <section className="border-b border-zinc-800/70 bg-gradient-to-b from-blue-950/30 to-transparent">
        <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16" data-testid="comeback-section">
          <div className="mx-auto max-w-3xl text-center animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-blue-300">
              <Sparkles size={13} /> {tr.comebackKicker}
            </span>
            <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-zinc-50 sm:text-4xl">
              {tr.comebackTitle}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-zinc-400">
              {tr.comebackBody}
            </p>
          </div>
        </div>
      </section>

      {/* hero */}
      <section className="relative overflow-hidden border-b border-zinc-800/70">
        <div className="pointer-events-none absolute inset-0 hero-grid" />
        <div className="relative mx-auto max-w-6xl px-5 py-16 sm:py-24">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-blue-300">
              <ShieldCheck size={13} /> {tr.heroBadge}
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-[1.1] tracking-tight text-zinc-50 sm:text-5xl">
              {tr.heroTitle}
            </h1>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-zinc-400">{tr.heroSubtitle}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button
                data-testid="hero-cta"
                onClick={() => scrollTo("report")}
                className="h-11 bg-blue-600 px-6 text-[14px] font-medium text-white hover:bg-blue-700"
              >
                {tr.heroCta} <ArrowRight size={16} />
              </Button>
              <span className="text-[12px] text-zinc-500">{tr.heroTrust}</span>
            </div>
          </div>
        </div>
      </section>

      {/* report form */}
      <section id="report" className="mx-auto max-w-3xl px-5 py-16">
        <AvailabilityBar />
        <div className="rounded-2xl border border-zinc-800/70 bg-[#111113] p-6 shadow-2xl shadow-black/40 sm:p-8">
          {result ? (
            <SuccessPanel result={result} onReset={() => setResult(null)} />
          ) : (
            <>
              <h2 className="text-xl font-semibold tracking-tight text-zinc-50">{tr.formTitle}</h2>
              <p className="mb-6 mt-1 text-[13px] text-zinc-500">{tr.formSubtitle}</p>
              <ReportForm onSuccess={setResult} />
            </>
          )}
        </div>
        <EventsSection compact limit={2} />
      </section>

      {/* how it works */}
      <section id="how" className="border-y border-zinc-800/70 bg-zinc-950">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-50">{tr.howTitle}</h2>
          <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-zinc-400">{tr.howIntro}</p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {steps.map((s, i) => (
              <div key={i} className="rounded-xl border border-zinc-800/70 bg-[#111113] p-6">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-600/15 text-blue-400">
                  <s.icon size={20} />
                </div>
                <div className="mt-4 text-[14px] font-semibold text-zinc-100">{s.t}</div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-400">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* team & new strategies */}
      <section id="team" className="border-b border-zinc-800/70 bg-zinc-950">
        <div className="mx-auto max-w-6xl px-5 py-16" data-testid="team-section">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-blue-300">
            <Sparkles size={13} /> {tr.teamKicker}
          </span>
          <h2 className="mt-4 text-2xl font-semibold tracking-tight text-zinc-50 sm:text-3xl">{tr.teamTitle}</h2>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-zinc-400">{tr.teamIntro}</p>

          {team.stats?.some((s) => s.value) && (
            <div className="mt-8" data-testid="team-stats">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {team.stats.filter((s) => s.value).map((s) => (
                  <div key={s.key} className="rounded-xl border border-zinc-800/70 bg-[#111113] p-5">
                    <div className="text-2xl font-bold tracking-tight text-blue-400">{s.value}</div>
                    <div className="mt-1 text-[12px] font-medium uppercase tracking-wider text-zinc-500">
                      {TEAM_STAT_LABELS[s.key]?.[lang] || TEAM_STAT_LABELS[s.key]?.en || s.key}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2.5 text-[11px] italic text-zinc-600">{tr.teamAutoNote}</p>
            </div>
          )}

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {strategies.map((s, i) => (
              <div key={i} className="rounded-xl border border-zinc-800/70 bg-[#111113] p-6 transition-colors hover:border-blue-500/40">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-600/15 text-blue-400">
                  <s.icon size={20} />
                </div>
                <div className="mt-4 text-[14px] font-semibold text-zinc-100">{s.t}</div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-400">{s.d}</p>
              </div>
            ))}
          </div>

          {team.members?.length > 0 && (
            <div className="mt-10" data-testid="team-members">
              <h3 className="text-[13px] font-semibold uppercase tracking-wider text-zinc-400">{tr.teamMembersTitle}</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {team.members.map((m, i) => (
                  <div key={i} className="flex gap-3.5 rounded-xl border border-zinc-800/70 bg-[#111113] p-5">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-blue-600/15 text-blue-400">
                      <UserRound size={20} />
                    </div>
                    <div className="min-w-0">
                      {m.name && <div className="text-[14px] font-semibold text-zinc-100">{m.name}</div>}
                      {m.role && <div className={`text-[13px] font-medium text-blue-300 ${m.name ? "" : "text-[14px] font-semibold text-zinc-100"}`}>{m.role}</div>}
                      {m.note && <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-400">{m.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* track */}
      <section id="track" className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="text-2xl font-semibold tracking-tight text-zinc-50">{tr.trackTitle}</h2>
        <p className="mb-6 mt-1 text-[13px] text-zinc-500">{tr.trackSub}</p>
        <TrackReport />
      </section>

      <SiteFooter />
    </div>
  );
}
